import { useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { OmitStrict } from "@amx/ux-twoui-utils";
import { HighContrastSelector, ITextStyles, Link } from "@fluentui/react";
import { Location, LocationDescriptor, LocationState } from "history";
import React, { useEffect } from "react";
import { Link as RouteLink } from "react-router-dom";
import { useComponentLogger } from "../../../shared/logging";
import { validateProps } from "../../../shared/props";
import { validateChildComponent } from "../../../shared/validation";
import { getFontRampStyle } from "../../../styles/fonts";
import { Heading } from "../../Heading/Heading";
import { Label } from "../../Label/Label";
import { TruncatedText } from "../../TruncatedText/TruncatedText";
import { BaseLinkProps, HyperlinkTarget } from "../shared/props";

export interface HyperlinkProps extends OmitStrict<BaseLinkProps, "children"> {
    /** URI of the link */
    readonly href: string;
    /** Optional callback for when the Hyperlink is clicked */
    readonly onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    readonly children: string | JSX.Element;
    /** Target where to display the Hyperlink. */
    readonly target?: HyperlinkTarget;
    /**
     * Whether to save the linked URL instead of navigating to it.
     * Provide a non empty string to name the downloaded file (including the file extension)
     * Providing an empty string will save the file with a default name (does not include a file extension)
     */
    readonly download?: string;
}

export interface RoutelinkProps extends BaseLinkProps {
    /** React route of the link */
    readonly to: LocationDescriptor<LocationState> | ((location: Location<LocationState>) => LocationDescriptor<LocationState>);

    /** Whether clicking the link will replace the current entry in the history stack instead of adding a new one */
    readonly replace?: boolean;
}

export const Hyperlink: React.FC<HyperlinkProps | RoutelinkProps> = ({
    children,
    disabled,
    truncatedLinkProps,
    textVariant = "bodyThemeText",
    underline,
    ...otherProps
}) => {
    useComponentLogger("Hyperlink", otherProps.id);

    const twoUITelemetry = useTwoUITelemetry();
    const theme = useTheme();

    const download = "download" in otherProps ? otherProps.download : undefined;

    let href = "href" in otherProps ? otherProps.href : undefined;
    let to: LocationDescriptor<LocationState> | ((location: Location<LocationState>) => LocationDescriptor<LocationState>) | undefined;
    let replace: boolean | undefined;

    let as: React.ComponentClass<any> | undefined;

    if (!href) {
        href = "#";

        if (!disabled) {
            as = RouteLink;
            to = "to" in otherProps ? otherProps.to : undefined;
            replace = "replace" in otherProps ? otherProps.replace : undefined;
        }
    }

    const { title, ...passThroughProps } = otherProps;
    delete passThroughProps["textVariant"]; // Handles console error so the prop doesn't pass on where it shouldn't be
    validateProps("Hyperlink", passThroughProps);
    const titleText = title || passThroughProps["aria-label"];

    if (typeof (children) !== "string") {
        validateChildComponent(children, [Heading, Label], "Hyperlink");
    }

    // To avoid security issues with window.opener API (https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#target)
    if ("target" in passThroughProps) {
        passThroughProps["rel"] = "noreferrer noopener";
    }

    const fontRampStyles = getFontRampStyle(theme, textVariant, undefined, disabled ? "disabled" : undefined);
    const linkStyles: ITextStyles = {
        root: {
            fontSize: fontRampStyles.fontSize,
            fontWeight: fontRampStyles.fontWeight,
            lineHeight: fontRampStyles.lineHeight,
            fontFamily: fontRampStyles.fontFamily,
            selectors : fontRampStyles.selectors,
            color: fontRampStyles.color,
            ":hover": {
                color: fontRampStyles.hoverColor
            },
            [HighContrastSelector]: {
                color: "linktext",
                ":focus > span": {
                    color: "Highlight",
                    outline: "1px solid Highlight"
                }
            }
        }
    };

    useEffect(() => {
        twoUITelemetry.checkUrlAndReportHttp(href);
    }, [href]);

    return (
        <Link
            href={href}
            title={titleText}
            disabled={disabled}
            styles={linkStyles}
            {...passThroughProps}
            as={as}
            to={to}
            replace={replace}
            download={download}
            underline={underline}
        >
            {truncatedLinkProps ? (
                <TruncatedText
                    lines={truncatedLinkProps.lines}
                    variant={textVariant}
                    stopPropagation={false}
                >
                    {children}
                </TruncatedText>
            ) :
                children
            }
        </Link>
    );
};
